import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { RefreshCw } from 'lucide-react';

/**
 * Error boundary specializzato per errori di caricamento chunk (code-split).
 *
 * Dopo un deploy i chunk vecchi (hash diversi) possono sparire: se l'utente
 * naviga verso una route lazy, React.lazy lancia un `ChunkLoadError`.
 * Invece di mostrare la pagina di errore generica, proviamo un reload automatico
 * una sola volta (flag su sessionStorage).
 */
class LazyRouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk [\d]+ failed/i.test(String(error?.message || '')) ||
      /Loading CSS chunk/i.test(String(error?.message || ''));

    if (!isChunkError) {
      // Propaga ad altri error boundary (es. globale)
      throw error;
    }

    try {
      const KEY = '__sp_chunk_reload_attempt__';
      const already = sessionStorage.getItem(KEY);
      if (!already) {
        sessionStorage.setItem(KEY, '1');
        // Reload soft: forza il fetch dei nuovi asset
        window.location.reload();
      }
    } catch {
      // sessionStorage non disponibile: reload comunque
      try {
        window.location.reload();
      } catch {
        /* noop */
      }
    }
  }

  handleReload = () => {
    try {
      sessionStorage.removeItem('__sp_chunk_reload_attempt__');
    } catch {
      /* noop */
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="p-6 text-center space-y-4">
              <h2 className="text-lg font-semibold">Aggiornamento disponibile</h2>
              <p className="text-sm text-muted-foreground">
                È stata rilasciata una nuova versione dell'app. Ricarica per continuare.
              </p>
              <Button onClick={this.handleReload} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Ricarica
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

export default LazyRouteErrorBoundary;
